import { Controller } from "@hotwired/stimulus"
import { parseSave, resolveItemAliases } from "../lib/castlevania_belmonts_curse_save"
import { createReport, ZOOM_STEPS } from "../lib/castlevania_belmonts_curse_report"
import { mapZoomWidth } from "../lib/castlevania_belmonts_curse_map"
import { defaultAdvancedMap, OBJECT_TYPES_BY_KEY } from "../lib/castlevania_belmonts_curse_objects"
import { readStorage, writeStorage } from "../lib/game_tool/storage"
import { nextTabId, tabId } from "../lib/game_tool/tabs"
import { rebaseImagePaths } from "../lib/static_images"

const STORAGE = { spoilers: "bc-spoilers", hideLore: "bc-hide-lore", adv: "bc-adv" }
const FILTER_STORAGE = { relics: "bc-relic-filter", spells: "bc-spell-filter", completion: "bc-completion-filter" }
const TAB_STORAGE = { main: "bc-tab" }
// The least parseSave accepts: a save with no player progress.
const EMPTY_SAVE = JSON.stringify({ playerSave: {} })
// Arcanas, Armoury and Relics were sub-tabs of an Equipment tab, remembered under this key.
const LEGACY_EQUIPMENT_TAB = "bc-equipment-tab"

// Castlevania: Belmont's Curse save analyzer (client-side only — never uploads).
export default class extends Controller {
  static targets = ["report"]
  static outlets = ["save-loader"]
  static values = { dataUrl: String }

  connect() {
    this.save = null
    this.report = null
    this.ui = {
      fileName: "",
      debug: new URLSearchParams(window.location.search).has("debug"),
      spoilers: readStorage(STORAGE.spoilers) === "1",
      hideLore: readStorage(STORAGE.hideLore) !== "0",
      filters: Object.fromEntries(Object.entries(FILTER_STORAGE).map(([key, storageKey]) => [key, readStorage(storageKey) || "all"])),
      tabs: Object.fromEntries(Object.entries(TAB_STORAGE).map(([group, key]) => [group, readStorage(key)])),
      world: null,
      zoom: 1,
      cardSides: {},
      adv: this.loadAdvancedMap(),
    }
    if (this.ui.tabs.main === "equipment") this.ui.tabs.main = readStorage(LEGACY_EQUIPMENT_TAB) || "arcanas"
    this.boundTipKeydown = (e) => this.onTipKeydown(e)
    this.loadGameData()
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundTipKeydown)
  }

  async loadGameData() {
    try {
      const response = await fetch(this.dataUrlValue)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      this.gameData = rebaseImagePaths(await response.json())
      this.report = createReport(this.gameData)
      this.saveLoaderOutlet.ready()
    } catch {
      this.showError("Couldn't load the game data this tool needs. Reload the page to try again.")
    }
  }

  // --- File loading (save-loader:loaded) -------------------------------------

  onSaveLoaded({ detail: { file, content } }) {
    if (!this.report) return
    try {
      this.showSave(parseSave(content), file.name)
    } catch (err) {
      this.showError(err.message || String(err))
    }
  }

  // save-loader:empty: nothing found yet, so spoilers decide what shows.
  onEmptySave() {
    if (this.report) this.showSave(parseSave(EMPTY_SAVE), "Empty save")
  }

  showSave(save, fileName) {
    resolveItemAliases(save.player, this.gameData)
    this.save = save
    this.ui.fileName = fileName
    this.ui.world = null
    this.ui.cardSides = {}
    this.render()
    this.saveLoaderOutlet.element.hidden = true
    this.reportTarget.hidden = false
    this.reportTarget.scrollIntoView({ block: "start" })
  }

  showError(message) {
    this.reportTarget.hidden = true
    this.saveLoaderOutlet.showError(message)
  }

  // --- Rendering -----------------------------------------------------------

  render() {
    // Keep the map's scroll position when redrawing the same area (e.g. toggling a category while zoomed).
    const wrap = this.reportTarget.querySelector("[data-map-wrap]")
    const keepScroll = wrap && wrap.dataset.mapWrap === this.ui.world ? [wrap.scrollLeft, wrap.scrollTop] : null
    // Collapses the user opened stay open across re-renders (settings, filters, spoilers).
    const openCollapses = [...this.reportTarget.querySelectorAll("details[open][data-collapse]")].map((d) => d.dataset.collapse)
    this.reportTarget.innerHTML = this.report.render(this.save, this.ui)
    for (const key of openCollapses) {
      const details = this.reportTarget.querySelector(`details[data-collapse="${CSS.escape(key)}"]`)
      if (details) details.open = true
    }
    const newWrap = this.reportTarget.querySelector("[data-map-wrap]")
    if (newWrap && keepScroll) [newWrap.scrollLeft, newWrap.scrollTop] = keepScroll
    // Esc closes the map dialog natively. `close` doesn't bubble, and arrives a task later.
    this.reportTarget.querySelector("[data-map-dialog]")?.addEventListener("close", () => this.onMapClosed())
  }

  /** Re-render, then put focus back on the control that triggered it (innerHTML replaces it). */
  renderKeepingFocus(selector) {
    this.render()
    if (selector) this.reportTarget.querySelector(selector)?.focus()
  }

  selectTab(group, id, focus) {
    this.ui.tabs[group] = id
    writeStorage(TAB_STORAGE[group], id)
    this.render()
    if (focus) document.getElementById(tabId("bc", group, id))?.focus()
  }

  // --- Report events (delegated from the report container) ------------------

  onReportClick(event) {
    const target = event.target
    const tab = target.closest("[data-tab]")
    if (tab) {
      this.selectTab(tab.dataset.tabGroup, tab.dataset.tab, true)
      return
    }
    const areaTab = target.closest("[data-world]")
    if (areaTab) {
      this.ui.world = areaTab.dataset.world
      this.renderKeepingFocus(`[data-world="${CSS.escape(this.ui.world)}"]`)
      return
    }
    // Opens or closes every level of the completion breakdown; render() keeps that state on redraw.
    const sections = target.closest("[data-completion-sections]")
    if (sections) {
      const open = sections.dataset.completionSections === "open"
      for (const details of this.reportTarget.querySelectorAll(".game-tool__completion details")) details.open = open
      return
    }
    if (target.closest("[data-load-another]")) {
      this.saveLoaderOutlet.chooseAnother()
      return
    }
    const zoom = target.closest("[data-zoom]")
    if (zoom) {
      this.zoomBy(Number(zoom.dataset.zoom))
      // Disabled buttons can't hold focus, so fall back to the other zoom button at the limits.
      if (zoom.disabled) zoom.parentElement.querySelector(`[data-zoom="${-Number(zoom.dataset.zoom)}"]`)?.focus()
      return
    }
    if (target.closest("[data-map-open]")) {
      this.openMap()
      return
    }
    // The dialog itself is only hit outside the panel's border; a drag that ends there doesn't count.
    if (target.closest("[data-map-close]") || (target.matches("[data-map-dialog]") && this.downOnBackdrop)) {
      target.closest("dialog").close()
      this.onMapClosed()
      return
    }
    const bulk = target.closest("[data-adv]")
    if (bulk) {
      // The bulk buttons only affect the categories present in this area.
      const keys = [...this.reportTarget.querySelectorAll("[data-cat]")].map((el) => el.dataset.cat)
      for (const key of keys) {
        const show = bulk.dataset.adv === "all" || (bulk.dataset.adv === "completion" && OBJECT_TYPES_BY_KEY[key].completion)
        show ? this.ui.adv.off.delete(key) : this.ui.adv.off.add(key)
      }
      this.ui.adv.bulk = bulk.dataset.adv
      this.saveAdvancedMap()
      this.renderKeepingFocus(`[data-adv="${bulk.dataset.adv}"]`)
    }
  }

  onReportChange(event) {
    const el = event.target
    let selector
    if (el.dataset.setting === "spoilers") {
      this.ui.spoilers = el.checked
      writeStorage(STORAGE.spoilers, el.checked ? "1" : "0")
      selector = '[data-setting="spoilers"]'
    } else if (el.dataset.setting === "showLore") {
      this.ui.hideLore = !el.checked
      writeStorage(STORAGE.hideLore, el.checked ? "0" : "1")
      selector = '[data-setting="showLore"]'
    } else if (el.dataset.setting === "filter") {
      const key = el.dataset.filter
      this.ui.filters[key] = el.value
      writeStorage(FILTER_STORAGE[key], el.value)
      selector = `[data-filter="${key}"][value="${el.value}"]`
    } else if (el.dataset.setting === "cardSide") {
      this.ui.cardSides[el.dataset.card] = el.checked ? "self" : "cursed"
      selector = `[data-card="${el.dataset.card}"]`
    } else if (el.dataset.setting === "advancedMap") {
      this.ui.adv.on = el.checked
      this.saveAdvancedMap()
      selector = '[data-setting="advancedMap"]'
    } else if (el.dataset.setting === "hideDone") {
      this.ui.adv.hideDone = el.checked
      this.saveAdvancedMap()
      selector = '[data-setting="hideDone"]'
    } else if (el.dataset.cat) {
      el.checked ? this.ui.adv.off.delete(el.dataset.cat) : this.ui.adv.off.add(el.dataset.cat)
      this.ui.adv.bulk = null
      this.saveAdvancedMap()
      selector = `[data-cat="${el.dataset.cat}"]`
    } else {
      return
    }
    this.renderKeepingFocus(selector)
  }

  // Arrow keys, Home and End move between tabs (WAI-ARIA tabs pattern).
  onReportKeydown(event) {
    if (!event.target.matches("[role=tab]")) return
    if (event.target.dataset.world) {
      const worlds = [...this.reportTarget.querySelectorAll("[role=tab][data-world]")].map((el) => el.dataset.world)
      const world = nextTabId(event, worlds, this.ui.world)
      if (!world) return
      this.ui.world = world
      this.renderKeepingFocus(`[data-world="${CSS.escape(world)}"]`)
      return
    }
    const group = event.target.dataset.tabGroup
    const id = nextTabId(event, this.report.tabIds(group, this.ui), this.ui.tabs[group])
    if (id) this.selectTab(group, id, true)
  }

  // --- Full-screen map, zoom and pan ----------------------------------------
  // The one map element moves between the panel and the dialog, so zoom and scroll carry over.

  openMap() {
    const dialog = this.reportTarget.querySelector("[data-map-dialog]")
    const wrap = this.reportTarget.querySelector("[data-map-wrap]")
    if (!dialog || !wrap) return
    this.hideMapTip()
    dialog.showModal()
    this.keepingMapPoint(() => dialog.querySelector("[data-map-dialog-body]").append(wrap))
    wrap.focus({ preventScroll: true })
  }

  /** Put the map back in the panel. Safe to call twice (close button, then the `close` event). */
  onMapClosed() {
    const main = this.reportTarget.querySelector(".game-tool__bc-map-main")
    const wrap = this.reportTarget.querySelector("[data-map-dialog] [data-map-wrap]")
    // A late `close` from an earlier X click must not undo a quick reopen.
    if (!main || !wrap || wrap.closest("dialog").open) return
    this.stopWheelZoom()
    this.setZoom(this.ui.zoom)
    this.hideMapTip()
    this.keepingMapPoint(() => main.prepend(wrap))
    this.reportTarget.querySelector("[data-map-open]")?.focus()
  }

  /**
   * Run `change` (a zoom or a move), keeping the map point under (clientX, clientY) where it was.
   * Without a point, whatever was in the middle of the view ends up in the middle again.
   */
  keepingMapPoint(change, clientX, clientY) {
    const svg = this.reportTarget.querySelector("[data-map-wrap] svg")
    if (!svg) return change()
    const wrap = svg.parentElement
    const centre = (box) => [box.left + box.width / 2, box.top + box.height / 2]
    const [x0, y0] = clientX === undefined ? centre(wrap.getBoundingClientRect()) : [clientX, clientY]
    const point = this.mapPointAt(svg, x0, y0)
    const [left, top] = [wrap.scrollLeft, wrap.scrollTop]
    change()
    // Moving the element to a new parent resets its scroll.
    ;[wrap.scrollLeft, wrap.scrollTop] = [left, top]
    const [x1, y1] = clientX === undefined ? centre(wrap.getBoundingClientRect()) : [clientX, clientY]
    this.placeMapPoint(svg, point, x1, y1)
  }

  /** Where (clientX, clientY) falls on the map, as fractions of its width and height. */
  mapPointAt(svg, clientX, clientY) {
    const box = svg.getBoundingClientRect()
    return [(clientX - box.left) / box.width, (clientY - box.top) / box.height]
  }

  /** Scroll so the map point from mapPointAt sits under (clientX, clientY). */
  placeMapPoint(svg, [fx, fy], clientX, clientY) {
    const box = svg.getBoundingClientRect()
    svg.parentElement.scrollLeft += box.left + fx * box.width - clientX
    svg.parentElement.scrollTop += box.top + fy * box.height - clientY
  }

  /** Resize the map in place (no re-render, so the dialog stays open). */
  setZoom(zoom, { announce = true } = {}) {
    this.ui.zoom = zoom
    const svg = this.reportTarget.querySelector("[data-map-wrap] svg")
    if (svg) {
      svg.style.width = mapZoomWidth(svg.viewBox.baseVal.width, svg.viewBox.baseVal.height, zoom)
      svg.parentElement.classList.toggle("is-zoomed", zoom > 1)
    }
    for (const button of this.reportTarget.querySelectorAll("[data-zoom]")) {
      button.disabled = Number(button.dataset.zoom) < 0 ? zoom <= ZOOM_STEPS[0] : zoom >= ZOOM_STEPS.at(-1)
    }
    // The label is a live region, so it only changes once a wheel zoom settles.
    if (announce) {
      for (const label of this.reportTarget.querySelectorAll("[data-zoom-label]")) label.textContent = `${Math.round(zoom * 100)}%`
    }
  }

  /** The buttons go to the next preset step up or down from wherever the wheel left the zoom. */
  zoomBy(step) {
    this.stopWheelZoom()
    const zoom = step > 0
      ? ZOOM_STEPS.find((z) => z > this.ui.zoom + 0.001)
      : ZOOM_STEPS.findLast((z) => z < this.ui.zoom - 0.001)
    if (zoom === undefined) return
    this.keepingMapPoint(() => this.setZoom(zoom))
  }

  // Wheel zoom is only in the full-screen map, and is continuous between the first and last steps.
  // Each event moves a target; animation frames ease towards it, so wheel notches glide like a trackpad.
  // At 100%, scrolling down is left alone.
  onReportWheel(event) {
    const wrap = event.target.closest?.("[data-map-dialog] [data-map-wrap]")
    if (!wrap) return
    const delta = event.deltaY * (event.deltaMode === WheelEvent.DOM_DELTA_LINE ? 16 : 1)
    const from = this.wheelZoom?.target ?? this.ui.zoom
    if (delta > 0 && from <= ZOOM_STEPS[0]) return
    event.preventDefault()
    const target = Math.min(ZOOM_STEPS.at(-1), Math.max(ZOOM_STEPS[0], from * Math.exp(-delta * 0.002)))
    const svg = wrap.querySelector("svg")
    const anchor = this.wheelZoom?.x === event.clientX && this.wheelZoom?.y === event.clientY
      ? this.wheelZoom.point
      : this.mapPointAt(svg, event.clientX, event.clientY)
    this.wheelZoom = { target, svg, point: anchor, x: event.clientX, y: event.clientY, frame: this.wheelZoom?.frame }
    this.wheelZoom.frame ||= requestAnimationFrame(() => this.stepWheelZoom())
  }

  stepWheelZoom() {
    const { target, svg, point, x, y } = this.wheelZoom
    const gap = target - this.ui.zoom
    const settled = Math.abs(gap) < 0.002
    this.setZoom(settled ? target : this.ui.zoom + gap * 0.3, { announce: settled })
    this.placeMapPoint(svg, point, x, y)
    if (settled) this.wheelZoom = null
    else this.wheelZoom.frame = requestAnimationFrame(() => this.stepWheelZoom())
  }

  stopWheelZoom() {
    if (!this.wheelZoom) return
    cancelAnimationFrame(this.wheelZoom.frame)
    this.wheelZoom = null
  }

  // Drag with the primary mouse button to pan a zoomed map. Touch already pans natively.
  onReportPointerDown(event) {
    this.downOnBackdrop = event.target.matches?.("[data-map-dialog]")
    const wrap = event.target.closest?.("[data-map-wrap]")
    if (!wrap || event.button !== 0 || event.pointerType !== "mouse") return
    if (wrap.scrollWidth <= wrap.clientWidth && wrap.scrollHeight <= wrap.clientHeight) return
    this.pan = { wrap, id: event.pointerId, x: event.clientX, y: event.clientY, left: wrap.scrollLeft, top: wrap.scrollTop, moved: false }
  }

  onReportPointerMove(event) {
    const pan = this.pan
    if (!pan || event.pointerId !== pan.id) return
    const dx = event.clientX - pan.x, dy = event.clientY - pan.y
    // A few pixels of slack so a plain click isn't a drag.
    if (!pan.moved && Math.hypot(dx, dy) < 4) return
    if (!pan.moved) {
      pan.moved = true
      pan.wrap.setPointerCapture(pan.id)
      pan.wrap.classList.add("is-panning")
      this.hideMapTip()
    }
    pan.wrap.scrollLeft = pan.left - dx
    pan.wrap.scrollTop = pan.top - dy
  }

  onReportPointerUp(event) {
    const pan = this.pan
    if (!pan || event.pointerId !== pan.id) return
    pan.wrap.classList.remove("is-panning")
    if (pan.wrap.hasPointerCapture(pan.id)) pan.wrap.releasePointerCapture(pan.id)
    this.pan = null
  }

  // --- Map tooltip ----------------------------------------------------------
  // SVG shapes can't hold the CSS-only .tooltip bubble, so one shared bubble per map is placed here.

  onReportHover(event) {
    const target = event.target.closest?.("[data-map-tip-for]")
    if (!target) return
    // The panel's bubble comes first; the dialog has its own so it shows in the top layer.
    const tip = (target.closest("[data-map-dialog]") || this.reportTarget).querySelector("[data-map-tip]")
    if (!tip) return
    this.fillMapTip(tip, target, event)
    tip.classList.add("is-visible")
    this.placeMapTip(tip, target, event)
    document.addEventListener("keydown", this.boundTipKeydown)
  }

  // Stays up while the pointer moves from a shape onto the bubble itself (WCAG 1.4.13).
  onReportHoverOut(event) {
    const to = event.relatedTarget
    if (to && to.closest?.("[data-map-tip], [data-map-tip-for]")) return
    this.hideMapTip()
  }

  /** The room's name, then everything drawn in the hovered cell (collected ones struck through). */
  fillMapTip(tip, target, event) {
    const isRoom = target.dataset.mapTipFor === "room"
    const cell = isRoom ? event.target.dataset.cell : target.dataset.cell
    const svg = target.closest("svg")
    const room = isRoom ? target : cell && svg.querySelector(`rect[data-cell="${cell}"]`)?.closest("[data-map-tip-for=room]")
    const things = cell ? [...svg.querySelectorAll(`[data-map-tip-for]:not([data-map-tip-for="room"])[data-cell="${cell}"]`)] : []
    if (!things.length && !isRoom) things.push(target)
    const line = (el, tag) => {
      const node = document.createElement(tag)
      node.textContent = el.dataset.label
      if (el.dataset.done !== undefined) node.classList.add("is-done")
      return node
    }
    const parts = []
    if (room) parts.push(line(room, "strong"))
    if (things.length) {
      const list = document.createElement("ul")
      list.append(...things.map((el) => line(el, "li")))
      parts.push(list)
    }
    tip.replaceChildren(...parts)
  }

  /** Below small shapes; rooms can be huge, so below the pointer where it entered them. */
  placeMapTip(tip, target, event) {
    const box = tip.offsetParent.getBoundingClientRect()
    const shape = target.getBoundingClientRect()
    const atPointer = target.dataset.mapTipFor === "room" && event.clientX !== undefined
    const x = atPointer ? event.clientX : shape.left + shape.width / 2
    const y = atPointer ? event.clientY + 12 : shape.bottom
    const half = tip.offsetWidth / 2
    tip.style.left = `${Math.min(Math.max(x - box.left, half), box.width - half)}px`
    tip.style.top = `${y - box.top}px`
  }

  hideMapTip() {
    for (const tip of this.reportTarget.querySelectorAll("[data-map-tip]")) tip.classList.remove("is-visible")
    document.removeEventListener("keydown", this.boundTipKeydown)
  }

  onTipKeydown(event) {
    if (event.key === "Escape") this.hideMapTip()
  }

  // --- Advanced map settings ----------------------------------------------

  loadAdvancedMap() {
    const defaults = defaultAdvancedMap()
    let stored = {}
    try {
      stored = JSON.parse(readStorage(STORAGE.adv) || "{}") || {}
    } catch {}
    return {
      on: !!(stored.on ?? defaults.on),
      off: stored.off ? new Set(stored.off) : defaults.off,
      hideDone: !!(stored.hideDone ?? defaults.hideDone),
    }
  }

  saveAdvancedMap() {
    const { on, off, hideDone } = this.ui.adv
    writeStorage(STORAGE.adv, JSON.stringify({ on, off: [...off], hideDone }))
  }
}
